import { Edge, Node } from '@xyflow/react';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { OperatorParamType } from './components/nodes/OperatorNode';
import { IODataType, IOInfo } from './components/nodes/types';
import { useEffect, useMemo } from 'react';

type Graph = {
  id: string;
  nodes: Node[];
  edges: Edge[];
};

type GraphData = {
  id: string;
  name?: string;
  graph?: Graph;
};

export type GraphListEntryInput = {
  id: string;
  dataType: IODataType;
};

export type GraphListEntry = {
  id: string;
  name?: string;
  inputs?: GraphListEntryInput[];
};

export type LLMType = 'openai' | 'openrouter' | 'custom';

export type ProjectData = {
  id: number;
  name: string;
  gpt_key?: string;
  gpt_key_limit?: number;
  llm_type: LLMType;
  llm_custom_url: string;
  llm_model: string;
};

export type NewProjectData = {
  name: string;
  project_token: string;
};

export type User = {
  id: number;
  name: string | null;
  is_admin: boolean;
  email: string | null;
  verified: boolean;
};

export type NodeDescription = {
  inputs: IOInfo[];
  outputs: IOInfo[];
  name: string;
  label: string;
  parameters: OperatorParamType[];
};

export type ProcessingStatus =
  | 'new'
  | 'pending'
  | 'success'
  | 'failed'
  | 'cancelled';

export type ProcessingTaskListEntry = {
  id: number;
  graph_id: string;
  graph_name?: string;
  status: ProcessingStatus;
  datasets: Record<number, string>;
  created_at: string;
  updated_at: string;
};

export type ProcessingTaskStats = {
  id: number;
  status: ProcessingStatus;
  graph_id: string;
  n_new: number;
  n_pending: number;
  n_success: number;
  n_failed: number;
  n_cancelled: number;
  n_total: number;
};

export type IOValuesType = string | boolean | null;

type NodeIO = {
  name: string | null;
  values: IOValuesType[];
};

export type CompletionUsage = {
  completion_tokens: number;
  prompt_tokens: number;
  total_tokens: number;
  approximate_cost: number;
};

export type ExecutionResult = {
  output: IOValuesType[];
  results: Record<string, NodeIO[]> | null;
  error: string | null;
  llm_stats: CompletionUsage | null;
  llm_node_stats: Record<string, CompletionUsage> | null;
};

export type SignUpParams = {
  email: string;
  password: string;
  user_name: string;
};

export type EndpointListEntry = {
  name: string;
  graph_id: string;
  graph_name: string;
};

export type EndpointListResponse = {
  endpoints: EndpointListEntry[];
};

export type EndpointData = {
  name: string;
  graph_id: string;
  token?: string;
};

export type DatasetListEntry = {
  id: number;
  name: string;
  n_entries: number;
};

const baseClient = axios.create({
  baseURL: '/api/',
});

export class Client {
  protected client: AxiosInstance;
  protected abortControllers: AbortController[];
  protected projectId: string | null;
  protected exclusiveRequests: Map<string, AbortController>;

  constructor(projectId: string | null) {
    this.client = baseClient;
    this.abortControllers = [];
    this.projectId = projectId;
    this.exclusiveRequests = new Map();
  }

  protected removeAC(abortController: AbortController) {
    this.abortControllers = this.abortControllers.filter(
      (ac) => ac !== abortController
    );
  }

  protected async withAbortController(
    url: string,
    func: (ac: AbortController) => Promise<any>,
    exclusive: boolean = false
  ) {
    const abortController = new AbortController();
    this.abortControllers.push(abortController);
    const key = url;
    if (exclusive) {
      const ac = this.exclusiveRequests.get(key);
      if (ac) {
        ac.abort();
        this.removeAC(ac);
      }
      this.exclusiveRequests.set(key, abortController);
    }
    try {
      return await func(abortController);
    } finally {
      this.removeAC(abortController);
      if (exclusive) {
        this.exclusiveRequests.delete(key);
      }
    }
  }

  async request(config: AxiosRequestConfig, exclusive: boolean = false) {
    return this.withAbortController(
      config.url || '',
      async (ac: AbortController) => {
        return await this.client.request({
          ...config,
          signal: ac.signal,
        });
      },
      exclusive
    );
  }

  async postForm(
    url: string,
    data: any,
    config?: AxiosRequestConfig,
    exclusive: boolean = false
  ) {
    return this.withAbortController(
      config?.url || '',
      async (ac: AbortController) => {
        return await this.client.postForm(url, data, {
          ...config,
          signal: ac.signal,
        });
      },
      exclusive
    );
  }

  async get(url: string, config?: AxiosRequestConfig) {
    return this.request({
      ...config,
      url,
      method: 'GET',
    });
  }

  async delete(url: string, config?: AxiosRequestConfig) {
    return this.request({
      ...config,
      url,
      method: 'DELETE',
    });
  }

  async post(url: string, data: any, config?: AxiosRequestConfig) {
    return this.request({
      ...config,
      url,
      data,
      method: 'POST',
    });
  }

  async patch(url: string, data: any, config?: AxiosRequestConfig) {
    return this.request({
      ...config,
      url,
      data,
      method: 'PATCH',
    });
  }

  abort() {
    this.abortControllers.forEach((ac) => {
      ac.abort();
    });
    this.abortControllers = [];
  }

  async listGraphs() {
    const response = await this.get(`/projects/${this.projectId}/graphs`);
    return response.data.graphs as GraphListEntry[];
  }

  async getGraph(graphId: string) {
    const response = await this.get(
      `/projects/${this.projectId}/graphs/${graphId}`
    );
    return response.data as GraphData;
  }

  async addGraph(name: string) {
    await this.post(`/projects/${this.projectId}/graphs`, { name });
  }

  async updateGraph(
    graphId: string,
    graphData: GraphData,
    exclusive: boolean = true
  ) {
    await this.request(
      {
        url: `/projects/${this.projectId}/graphs/${graphId}`,
        method: 'PATCH',
        data: graphData,
      },
      exclusive
    );
  }

  async deleteGraph(graphId: string) {
    await this.delete(`/projects/${this.projectId}/graphs/${graphId}`);
  }

  async executeGraph(
    graphId: string,
    inputData: Record<string, any>,
    exclusive: boolean = true
  ) {
    const response = await this.postForm(
      `/projects/${this.projectId}/graphs/${graphId}/execute`,
      inputData,
      {},
      exclusive
    );
    const respData = response.data as ExecutionResult;
    return respData;
  }
  async listProjects() {
    const response = await this.get('/projects');
    return response.data.projects as ProjectData[];
  }

  async getProject() {
    const response = await this.get(`/projects/${this.projectId}`);
    return response.data as ProjectData;
  }

  async addProject(data: NewProjectData) {
    return await this.post(`/projects`, data);
  }

  async updateProject(projectData: ProjectData) {
    await this.patch(`/projects/${this.projectId}`, projectData);
  }

  async deleteProject() {
    await this.delete(`/projects/${this.projectId}`);
  }

  async exportProject() {
    const resp = await this.request(
      { url: `/projects/${this.projectId}/export`, method: 'POST' },
      true
    );
    return resp.data;
  }

  async importProject(jsonData: string) {
    const resp = await this.request(
      {
        url: `/projects/${this.projectId}/import`,
        method: 'POST',
        data: jsonData,
        headers: {
          'Content-Type': 'application/json',
        },
      },
      true
    );
    return resp.data;
  }

  async getUser() {
    const resp = await this.get(`/user`);
    return resp.data as User;
  }

  async signUp(params: SignUpParams) {
    const resp = await this.post('/sign-up', params);
    return resp.data as User;
  }

  async signUpGoogle(googleId: string) {
    const resp = await this.post('/sign-up-google', { credential: googleId });
    return resp.data as User;
  }

  async loginWithPassword(email: string, password: string) {
    const resp = await this.post('/login', { email, password });
    return resp.data as User;
  }

  async logout() {
    await this.post('/logout', null);
  }

  async resendVerificationEmail() {
    const resp = await this.post('/user/resend-verification-email', null);
    return resp.data as number;
  }

  async getVerificationEmailTimeout() {
    const resp = await this.get('/user/verification-email-timeout');
    return resp.data as number;
  }

  async getOperatorNodesDescriptions() {
    const resp = await this.get('/operator-nodes');
    return resp.data as NodeDescription[];
  }

  async addEndpoint(data: EndpointData) {
    await this.post(`/projects/${this.projectId}/endpoints`, data);
  }

  async updateEndpoint(endpointName: string, data: EndpointData) {
    await this.patch(
      `/projects/${this.projectId}/endpoints/${endpointName}`,
      data
    );
  }

  async deleteEndpoint(endpointId: string) {
    await this.delete(`/projects/${this.projectId}/endpoints/${endpointId}`);
  }

  async listEndpoints() {
    const resp = await this.get(`/projects/${this.projectId}/endpoints`);
    const respData: EndpointListResponse = resp.data;
    return respData.endpoints;
  }

  async listDatasets() {
    const resp = await this.get(`/projects/${this.projectId}/datasets`);
    return resp.data as DatasetListEntry[];
  }

  async deleteDataset(datasetId: number) {
    await this.delete(`/projects/${this.projectId}/datasets/${datasetId}`);
  }

  async createDataset(name: string, csvFile: File) {
    await this.postForm(`/projects/${this.projectId}/datasets`, {
      name,
      file: csvFile,
    });
  }

  async listTasks() {
    const resp = await this.get(`/projects/${this.projectId}/tasks`);
    return resp.data as ProcessingTaskListEntry[];
  }

  async getTaskStats(taskId: number) {
    const resp = await this.get(
      `/projects/${this.projectId}/tasks/${taskId}/stats`
    );
    return resp.data as ProcessingTaskStats;
  }

  async createTask(graphId: string, datasets: number[]) {
    const resp = await this.post(`/projects/${this.projectId}/tasks`, {
      graph_id: graphId,
      datasets,
    });
    return resp.data.id as number;
  }

  async deleteTask(taskId: number) {
    const resp = await this.delete(
      `/projects/${this.projectId}/tasks/${taskId}`
    );
    return resp.data.deleted as boolean;
  }

  async rerunFailedTask(taskId: number) {
    const resp = await this.post(
      `/projects/${this.projectId}/tasks/${taskId}/rerun-failed`,
      null
    );
    return resp.data.n_reruned as number;
  }

  async cancelTask(taskId: number) {
    const resp = await this.post(
      `/projects/${this.projectId}/tasks/${taskId}/cancel`,
      null
    );
    return resp.data.cancelled as boolean;
  }
}

export function useClient(projectId: string | null) {
  const client = useMemo<Client>(() => new Client(projectId), [projectId]);
  useEffect(
    () => () => {
      client.abort();
    },
    [client]
  );
  return client;
}
